import { FilePlus2, Pencil } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { DisabledTooltipButton } from '@/components/ui/disabled-tooltip-button';

import { DiscardInvoiceButton } from '../dialogs/discard-invoice';
import { invoiceVerbFor, type InvoiceActionKey } from '../lib/invoice-actions';
import { issueActionCopy } from '../lib/issue-action-copy';
import type { InvoiceRow } from '../types';

// Receivables read "issued"; payables read "recorded" — keep the lock copy in
// the invoice's own vocabulary rather than a fixed "issued".
function lockedTooltip(invoice: InvoiceRow): string {
  return `Invoice is ${issueActionCopy(invoice).pastAdjective} — economic changes require a Credit or Debit Note.`;
}

// The Content verb: one "Change values" entry, state-routed by the action
// model — edit the Draft, correct the frozen issued payable, or complete a
// placeholder stub. Locked rows keep the explanatory tooltip.
function ChangeValuesTrigger({
  invoice,
  route,
  onOpen,
}: {
  readonly invoice: InvoiceRow;
  readonly route: InvoiceActionKey | null;
  readonly onOpen: () => void;
}) {
  const label = `Change values on ${invoice.invoice_number ?? invoice.id}`;
  const testId = `bl-invoice-row-${invoice.id}-edit`;
  if (route === null) {
    return (
      <DisabledTooltipButton
        label={label}
        testId={testId}
        variant="outline"
        tooltip={lockedTooltip(invoice)}
        wrapperClassName="flex-1"
        className="w-full gap-1"
      >
        <Pencil className="size-4" />
        Change values
      </DisabledTooltipButton>
    );
  }
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="flex-1 gap-1"
      aria-label={label}
      data-testid={testId}
      onClick={onOpen}
    >
      <Pencil className="size-4" />
      Change values
    </Button>
  );
}

// The Chain verb: from an issued trade invoice, start a Credit or Debit Note
// whose predecessor is this invoice by construction (no parent picker).
function NewFollowOnButton({
  invoice,
  onCreateNext,
}: {
  readonly invoice: InvoiceRow;
  readonly onCreateNext: () => void;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="flex-1 gap-1"
      aria-label={`New follow-on note from ${invoice.invoice_number ?? invoice.id}`}
      data-testid={`bl-invoice-row-${invoice.id}-create-next`}
      onClick={onCreateNext}
    >
      <FilePlus2 className="size-4" />
      New follow-on
    </Button>
  );
}

export interface RowTriggersProps {
  readonly invoice: InvoiceRow;
  // Where the Change-values verb routes in the row's current state, or null
  // when the row is locked (issued receivable).
  readonly changeRoute: InvoiceActionKey | null;
  readonly creatable: boolean;
  readonly onChangeValues: () => void;
  readonly onCreateNext: () => void;
  readonly onDiscarded: () => void;
}

// The row's grouped verbs: Change values (Content), New follow-on (Chain),
// and Discard (Lifecycle — routes to cancel for Drafts, soft delete for
// never-issued imported rows, and disappears on locked rows).
export function RowTriggers(props: RowTriggersProps) {
  const discardable = invoiceVerbFor(props.invoice, 'discard') !== null;
  return (
    <>
      <ChangeValuesTrigger
        invoice={props.invoice}
        route={props.changeRoute}
        onOpen={props.onChangeValues}
      />
      {props.creatable && (
        <NewFollowOnButton invoice={props.invoice} onCreateNext={props.onCreateNext} />
      )}
      {discardable && (
        <DiscardInvoiceButton invoice={props.invoice} onDiscarded={props.onDiscarded} iconOnly />
      )}
    </>
  );
}
